import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { ApiError } from '@/shared/api/client';
import {
  brokers,
  type BankAccount,
  type Broker,
  type BrokerBankAccountEntry,
  type BrokerResponsible,
  type BrokerResponsibleInput,
  type BrokerResponsibleKind,
  type FinancialContact,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatCpfCnpj, formatDateTime, formatPhone } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { DataTable } from '@/shared/ui/DataTable';
import { DetailCard } from '@/shared/ui/DetailLayout';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { ConfirmDialog, Modal } from '@/shared/ui/Modal';
import { Select } from '@/shared/ui/Select';
import { useToast } from '@/shared/ui/useToast';

import { fieldErrorOf } from '../shared/fieldError';

import {
  descreveConta,
  dicaDadosBancarios,
  permissaoDadosBancarios,
} from './contaBancaria';
import { FinancialDataFields } from './FinancialDataFields';
import {
  PERMISSAO_EDITAR,
  rotuloDoTipoDeResponsavel,
  tiposDeResponsavel,
} from './rotulos';

/**
 * Subaba "Dados financeiros": a conta de repasse e o responsavel financeiro, com o
 * botao "Salvar dados financeiros" do OnPoint; o Pix, que tambem e destino do
 * repasse; o historico das contas; e os demais responsaveis da corretora.
 *
 * A conta e o Pix tem alcada propria (corretores.dados_bancarios.update, ADR 0019).
 * Sem ela, ficam so de leitura, e o responsavel financeiro continua editavel.
 */
export function BrokerFinancialSection({ corretor }: { corretor: Broker }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const podeEditar = can(PERMISSAO_EDITAR);
  const podeDadosBancarios = podeEditar && can(permissaoDadosBancarios);

  const [conta, setConta] = useState<BankAccount>(corretor.bank_account);
  const [responsavel, setResponsavel] = useState<FinancialContact>(
    corretor.financial_contact,
  );
  const [pixKind, setPixKind] = useState(corretor.pix_kind);
  const [pixKey, setPixKey] = useState(corretor.pix_key);

  const salvar = useMutation({
    mutationFn: () =>
      brokers.update(corretor.id, {
        financial_contact: responsavel,
        ...(podeDadosBancarios
          ? { bank_account: conta, pix_kind: pixKind, pix_key: pixKey }
          : {}),
      }),
    onSuccess: () => {
      toast.success('Dados financeiros salvos.');
      void queryClient.invalidateQueries({ queryKey: ['broker', corretor.id] });
      void queryClient.invalidateQueries({ queryKey: ['brokers'] });
      void queryClient.invalidateQueries({
        queryKey: ['entity-history', 'broker', corretor.id],
      });
    },
  });
  const erro = salvar.error instanceof ApiError ? salvar.error : null;

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    salvar.mutate();
  }

  return (
    <div className="flex flex-col gap-4">
      <form onSubmit={enviar}>
        <DetailCard
          title="Dados financeiros"
          description="A conta que recebe o repasse da comissao e quem responde pelo financeiro da corretora."
        >
          {salvar.error ? (
            <Alert tone="danger" title="Nao foi possivel salvar">
              {salvar.error.message}
            </Alert>
          ) : null}
          <fieldset
            disabled={!podeEditar || salvar.isPending}
            className="flex flex-col gap-5"
          >
            <FinancialDataFields
              conta={conta}
              responsavel={responsavel}
              erro={erro}
              contaSomenteLeitura={!podeDadosBancarios}
              onContaChange={setConta}
              onResponsavelChange={setResponsavel}
            />
            <fieldset
              disabled={!podeDadosBancarios}
              className="grid gap-4 sm:grid-cols-2"
            >
              <legend className="text-xs font-semibold text-ink-700">
                Pix de recebimento
              </legend>
              {!podeDadosBancarios ? (
                <p className="text-2xs text-ink-500 sm:col-span-2">
                  {dicaDadosBancarios}
                </p>
              ) : null}
              <Select
                label="Forma de recebimento"
                value={pixKind}
                placeholder="Nao informada"
                options={[
                  { value: 'cnpj', label: 'Pix por CNPJ' },
                  { value: 'cpf', label: 'Pix por CPF' },
                  { value: 'email', label: 'Pix por e-mail' },
                  { value: 'telefone', label: 'Pix por telefone' },
                  { value: 'aleatoria', label: 'Pix por chave aleatoria' },
                ]}
                error={fieldErrorOf(erro, 'pix_kind')}
                onChange={(evento) => {
                  setPixKind(evento.target.value);
                  if (evento.target.value === '') setPixKey('');
                }}
              />
              <Field
                label="Chave de recebimento"
                value={pixKey}
                disabled={pixKind === ''}
                error={fieldErrorOf(erro, 'pix_key')}
                onChange={(evento) => setPixKey(evento.target.value)}
              />
            </fieldset>
          </fieldset>
          {podeEditar ? (
            <div className="flex justify-end">
              <Button type="submit" loading={salvar.isPending}>
                Salvar dados financeiros
              </Button>
            </div>
          ) : null}
        </DetailCard>
      </form>

      <BankAccountHistory corretoraId={corretor.id} />
      <ResponsiblesCard corretoraId={corretor.id} podeEditar={podeEditar} />
    </div>
  );
}

function BankAccountHistory({ corretoraId }: { corretoraId: string }) {
  const consulta = useQuery({
    queryKey: ['broker', corretoraId, 'bank-accounts'],
    queryFn: ({ signal }) => brokers.bankAccounts(corretoraId, signal),
  });
  return (
    <DetailCard
      title="Historico das contas"
      description="Cada conta que ja foi o destino do repasse. A troca so acontece pelos dados financeiros, com a alcada propria."
      loading={consulta.isPending}
      error={consulta.error}
    >
      <DataTable
        columns={[
          {
            key: 'conta',
            header: 'Conta',
            render: (linha: BrokerBankAccountEntry) =>
              descreveConta(linha.bank_account),
          },
          {
            key: 'situacao',
            header: 'Situacao',
            render: (linha: BrokerBankAccountEntry) =>
              linha.is_current ? (
                <Badge tone="success">Vigente</Badge>
              ) : (
                <Badge tone="neutral">
                  Trocada em {formatDateTime(linha.replaced_at)}
                </Badge>
              ),
          },
          {
            key: 'gravada',
            header: 'Gravada por',
            hideBelow: 'md',
            render: (linha: BrokerBankAccountEntry) =>
              `${linha.created_label || 'sistema'} em ${formatDateTime(linha.created_at)}`,
          },
        ]}
        items={consulta.data ?? []}
        keyOf={(linha) => linha.id}
        empty={<EmptyState title="Nenhuma conta registrada" />}
      />
    </DetailCard>
  );
}

function ResponsiblesCard({
  corretoraId,
  podeEditar,
}: {
  corretoraId: string;
  podeEditar: boolean;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [editando, setEditando] = useState<BrokerResponsible | 'novo' | null>(null);
  const [removendo, setRemovendo] = useState<BrokerResponsible | null>(null);

  const consulta = useQuery({
    queryKey: ['broker', corretoraId, 'responsibles'],
    queryFn: ({ signal }) => brokers.responsibles(corretoraId, signal),
  });

  const remover = useMutation({
    mutationFn: (id: string) => brokers.removeResponsible(corretoraId, id),
    onSuccess: () => {
      toast.success('Responsavel excluido.');
      setRemovendo(null);
      void queryClient.invalidateQueries({ queryKey: ['broker', corretoraId] });
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  return (
    <DetailCard
      title="Outros responsaveis"
      description="Responsaveis da corretora por tipo, como o responsavel tecnico."
      loading={consulta.isPending}
      error={consulta.error}
      action={
        podeEditar ? (
          <Button size="sm" variant="secondary" onClick={() => setEditando('novo')}>
            Adicionar responsavel
          </Button>
        ) : undefined
      }
    >
      <DataTable
        columns={[
          {
            key: 'tipo',
            header: 'Tipo',
            render: (linha: BrokerResponsible) => rotuloDoTipoDeResponsavel(linha.kind),
          },
          {
            key: 'nome',
            header: 'Nome',
            render: (linha: BrokerResponsible) => linha.name,
          },
          {
            key: 'documento',
            header: 'CPF/CNPJ',
            render: (linha: BrokerResponsible) =>
              linha.document ? formatCpfCnpj(linha.document) : '',
          },
          {
            key: 'contato',
            header: 'Contato',
            hideBelow: 'md',
            render: (linha: BrokerResponsible) =>
              [linha.email, linha.phone ? formatPhone(linha.phone) : '']
                .filter(Boolean)
                .join(', '),
          },
          ...(podeEditar
            ? [
                {
                  key: 'acoes',
                  header: 'Acoes',
                  render: (linha: BrokerResponsible) => (
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditando(linha)}
                      >
                        Editar
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setRemovendo(linha)}
                      >
                        Excluir
                      </Button>
                    </div>
                  ),
                },
              ]
            : []),
        ]}
        items={consulta.data ?? []}
        keyOf={(linha) => linha.id}
        empty={<EmptyState title="Nenhum outro responsavel cadastrado" />}
      />

      {editando ? (
        <ResponsibleDialog
          corretoraId={corretoraId}
          responsavel={editando === 'novo' ? undefined : editando}
          onClose={() => setEditando(null)}
          onSalvo={() => {
            setEditando(null);
            void queryClient.invalidateQueries({ queryKey: ['broker', corretoraId] });
          }}
        />
      ) : null}

      <ConfirmDialog
        open={removendo !== null}
        onClose={() => setRemovendo(null)}
        onConfirm={() => removendo && remover.mutate(removendo.id)}
        title="Excluir o responsavel?"
        message={`${removendo?.name ?? ''} sai da lista. A exclusao fica no historico do corretor.`}
        confirmLabel="Excluir"
        tone="danger"
        loading={remover.isPending}
      />
    </DetailCard>
  );
}

function ResponsibleDialog({
  corretoraId,
  responsavel,
  onClose,
  onSalvo,
}: {
  corretoraId: string;
  responsavel: BrokerResponsible | undefined;
  onClose: () => void;
  onSalvo: () => void;
}) {
  const toast = useToast();
  const [tipo, setTipo] = useState<BrokerResponsibleKind>(
    responsavel?.kind ?? 'tecnico',
  );
  const [nome, setNome] = useState(responsavel?.name ?? '');
  const [documento, setDocumento] = useState(responsavel?.document ?? '');
  const [email, setEmail] = useState(responsavel?.email ?? '');
  const [telefone, setTelefone] = useState(responsavel?.phone ?? '');

  const salvar = useMutation({
    mutationFn: (entrada: BrokerResponsibleInput) =>
      responsavel
        ? brokers.updateResponsible(corretoraId, responsavel.id, entrada)
        : brokers.addResponsible(corretoraId, entrada),
    onSuccess: () => {
      toast.success(responsavel ? 'Responsavel alterado.' : 'Responsavel incluido.');
      onSalvo();
    },
  });
  const erro = salvar.error instanceof ApiError ? salvar.error : null;

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    salvar.mutate({
      kind: tipo,
      name: nome,
      document: documento,
      email,
      phone: telefone,
    });
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={responsavel ? `Alterar ${responsavel.name}` : 'Adicionar responsavel'}
      size="md"
    >
      <form onSubmit={enviar} className="flex flex-col gap-4">
        {erro ? (
          <Alert tone="danger" title="Nao foi possivel salvar">
            {erro.message}
          </Alert>
        ) : null}
        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Tipo de responsavel"
            options={tiposDeResponsavel}
            value={tipo}
            error={fieldErrorOf(erro, 'kind')}
            onChange={(evento) => setTipo(evento.target.value as BrokerResponsibleKind)}
          />
          <Field
            label="Nome"
            required
            value={nome}
            error={fieldErrorOf(erro, 'name')}
            onChange={(evento) => setNome(evento.target.value)}
          />
          <Field
            label="CPF/CNPJ"
            value={documento}
            error={fieldErrorOf(erro, 'document')}
            onChange={(evento) => setDocumento(evento.target.value)}
          />
          <Field
            label="Email"
            type="email"
            value={email}
            error={fieldErrorOf(erro, 'email')}
            onChange={(evento) => setEmail(evento.target.value)}
          />
          <Field
            label="Telefone"
            value={telefone}
            error={fieldErrorOf(erro, 'phone')}
            onChange={(evento) => setTelefone(evento.target.value)}
          />
        </div>
        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" loading={salvar.isPending}>
            Salvar
          </Button>
        </div>
      </form>
    </Modal>
  );
}
